/**
 * Картка необоротного активу — ПОВНОЦІННА сторінка (рішення власника
 * 2026-08-27, замість попапа): реквізити + блок «Дії» як у 1С (введення в
 * експлуатацію, переміщення, модернізація, списання, продаж, інвентаризація,
 * зміна стану, зміна строку) + вкладки «Амортизація» і «Документи».
 * Дії ведуть на сторінки документів з ?asset_id= — модалка відкривається
 * одразу з цим активом у рядках.
 *
 * ч.915 (новий дизайн, Закон дизайну #10): картка довідника — DocLayout /
 * DocBlock (без номерів) / DocRail. Дії по активу («ще дії») — у «Повʼязаних
 * діях» правої рейки; вкладки картки працюють як і раніше, вміст кожної —
 * у блоках.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assetCardPayload } from './assetCardPayload';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { departmentsApi, employees as employeesApi } from '@/api/hr';
import {
  assetAccountSettings, assetGroups, depreciationMethods, fixedAssets, intangibleAssets, lowValueAssets,
  type AssetKind, type AssetStatus, type FixedAsset, type LowValueAsset,
} from '@/api/assets';
import {
  ASSET_KIND_UA, ASSET_STATUS_UA, DOC_TYPE_UA, assetsRegistry,
} from '@/api/assetsRegistry';
import { refs as refsApi } from '@/api/refs';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import ImportedBadge from '@/shared/components/ImportedBadge';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import TchTable from '@/shared/components/TchTable';

const fmt2 = (v: string | number | null | undefined) => v == null ? '—'
  : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PERM_BY_KIND: Record<AssetKind, string> = {
  FIXED_ASSET: 'refs.fixed_assets.manage',
  INTANGIBLE: 'refs.intangible_assets.manage',
  LOW_VALUE: 'refs.low_value_assets.manage',
};
// ч.915 (новий дизайн, Закон #10): тип сутності картки для «Історії документа»
// правої рейки. Власного рядка типу сторінка не мала (без дт-кт/історії/
// вкладень), тож беремо таблицю картки — саме так аудит-журнал пише entity_type
// (TG_TABLE_SCHEMA.TG_TABLE_NAME). Залежить від виду активу, як і PERM_BY_KIND.
const ENTITY_BY_KIND: Record<AssetKind, string> = {
  FIXED_ASSET: 'ref.fixed_assets_v2',
  INTANGIBLE: 'ref.intangible_assets_v2',
  LOW_VALUE: 'ref.low_value_assets',
};
// ч.915: блокування форми вибулого активу — fieldset у КОЖНОМУ блоці
// (канон AssetDocEditors): шапка блоку лишається поза fieldset.
const FIELDSET_STYLE = { border: 'none', margin: 0, padding: 0, minWidth: 0 } as const;

type AnyAsset = FixedAsset & Partial<LowValueAsset> & {
  external_source?: string | null;
  elapsed_depreciation_periods?: number | null;
};

export default function AssetCardPage() {
  const { kind: kindRaw, id } = useParams<{ kind: string; id: string }>();
  const kind = (['FIXED_ASSET', 'INTANGIBLE', 'LOW_VALUE'].includes(kindRaw ?? '')
    ? kindRaw : 'FIXED_ASSET') as AssetKind;
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isLv = kind === 'LOW_VALUE';
  const apiOf = kind === 'FIXED_ASSET' ? fixedAssets
    : kind === 'INTANGIBLE' ? intangibleAssets : (lowValueAssets as never as typeof fixedAssets);
  const { availableOrgs } = usePermissionsByOrg(PERM_BY_KIND[kind]);

  const assetQ = useQuery({
    queryKey: ['asset-card', kind, id],
    queryFn: () => apiOf.get(id!) as Promise<AnyAsset>,
    enabled: !isNew,
  });
  const asset = assetQ.data ?? null;
  const histQ = useQuery({
    queryKey: ['asset-history', kind, id],
    queryFn: () => assetsRegistry.history(kind, id!),
    enabled: !isNew,
  });
  const docRows = histQ.data?.documents ?? [];

  const [f, setF] = useState(() => emptyForm());
  const [tab, setTab] = useState<'main' | 'accounts' | 'docs'>('main');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (asset) setF(fromAsset(asset)); }, [asset]);
  // ч.1059: чернетка незбережених правок картки (як у документах).
  const draft = useFormDraft(f, setF, {
    ready: isNew || !!asset,
    subject: 'цієї картки',
    clearOnAnyMutation: false,
  });

  const groupsQ = useQuery({
    queryKey: ['asset-groups', kind],
    queryFn: () => assetGroups.list({ kind, limit: 200 }), enabled: !isLv,
  });
  const methodsQ = useQuery({
    queryKey: ['depreciation-methods'],
    queryFn: () => depreciationMethods.list({ limit: 100 }), enabled: !isLv,
  });
  const orgIdEff = f.organization_id || asset?.organization_id || availableOrgs[0]?.id || '';
  // 0453: податкове призначення (ПДВ) активу.
  const taxPurposesQ = useQuery({
    queryKey: ['tax-purposes-assets-costs'],
    queryFn: refsApi.taxPurposesAssetsCosts,
  });
  const acctSettingsQ = useQuery({
    queryKey: ['asset-account-settings', orgIdEff],
    queryFn: () => assetAccountSettings.get(orgIdEff),
    enabled: !!orgIdEff,
  });
  const departmentsQ = useQuery({
    queryKey: ['departments', orgIdEff],
    queryFn: () => departmentsApi.list({ organization_id: orgIdEff, limit: 200 }).then((r) => r.items),
    enabled: !!orgIdEff,
  });
  const employeesQ = useQuery({
    queryKey: ['employees-all', orgIdEff],
    queryFn: () => employeesApi.list({ organization_id: orgIdEff, limit: 500 }).then((r) => r.items),
    enabled: !!orgIdEff,
  });

  const save = useMutation({
    mutationFn: () => {
      // ч.1117: що саме йде на сервер — у assetCardPayload (з тестом контракту).
      const payload = assetCardPayload(f, { orgId: orgIdEff, isLv, forUpdate: !!asset });
      return asset ? apiOf.update(asset.id, payload as never) : apiOf.create(payload as never);
    },
    onSuccess: (saved: { id: string }) => {
      draft.markSaved();
      qc.invalidateQueries({ queryKey: ['asset-card', kind, saved.id] });
      qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] });
      qc.invalidateQueries({ queryKey: ['intangible-assets-v2'] });
      qc.invalidateQueries({ queryKey: ['low-value-assets'] });
      if (isNew) navigate(`/assets/card/${kind}/${saved.id}`, { replace: true });
    },
    onError: (e: unknown) => {
      const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof d === 'string' ? d : JSON.stringify(d ?? String(e)));
    },
  });
  const remove = useMutation({
    mutationFn: () => apiOf.remove(asset!.id),
    onSuccess: () => { draft.markSaved(); navigate('/assets/registry'); },
    onError: (e: unknown) => {
      const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof d === 'string' ? d : JSON.stringify(d ?? String(e)));
    },
  });

  const canSave = !!f.name.trim() && !!orgIdEff && (isLv || (!!f.asset_group_id && !!f.depreciation_method_id));
  const status = asset?.status as AssetStatus | undefined;
  const isRetired = status === 'WRITTEN_OFF' || status === 'SOLD';
  // 0447: діючі рахунки для вкладки «Рахунки обліку» (картка > орг > група > типовий).
  const _grp = (groupsQ.data?.items ?? []).find((g) => g.id === f.asset_group_id);
  const _ov = (acctSettingsQ.data?.group_overrides ?? []).find((o) => o.asset_group_id === f.asset_group_id);
  const effAcc = f.account_code.trim()
    ? { code: f.account_code.trim(), src: 'картка' }
    : isLv
      ? (acctSettingsQ.data?.lv_account_code
          ? { code: acctSettingsQ.data.lv_account_code, src: 'налаштування' }
          : { code: '22', src: 'типовий' })
      : (_ov?.account_code
          ? { code: _ov.account_code, src: 'налаштування орг' }
          : { code: _grp?.accounting_account_code ?? (kind === 'FIXED_ASSET' ? '10х' : '12х'), src: 'група' });
  const effDepr = f.depreciation_account_code.trim()
    ? { code: f.depreciation_account_code.trim(), src: 'картка' }
    : (_ov?.depreciation_account_code
        ? { code: _ov.depreciation_account_code, src: 'налаштування орг' }
        : { code: _grp?.depreciation_account_code ?? (kind === 'FIXED_ASSET' ? '131' : '133'), src: 'група' });
  // ч.639: колишній блок «Дії» — «ще дії» нижньої плашки. ч.915 (новий дизайн,
  // Закон #10): ТІ САМІ дії дослівно (склад, порядок, умови, переходи) — тепер
  // «Повʼязані дії» правої рейки (DocRail); на плашці «ще дії» немає (railMode).
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = isNew ? [] : [
    { label: 'введення в експлуатацію', disabled: status !== 'PARKED', onClick: () => act('/assets/commissioning') },
    { label: 'внутрішнє переміщення', disabled: isRetired, onClick: () => act('/assets/transfer') },
    { label: 'інвентаризація', disabled: isRetired, onClick: () => act('/assets/inventory') },
    // ч.916 (рішення власника): модернізація / консервація-ремонт-повернення /
    // зміна строку / зміна параметрів амортизації вели на ОДНУ сторінку-хаб
    // «Зміна стану» → одна дія; вид операції обирається у самому документі.
    // Заодно виправлено предвибір: раніше у URL йшло два `kind` (вид активу
    // перебивав вид операції) — хаб завжди відкривався з дефолтом.
    ...(!isLv ? [{
      label: 'зміна стану',
      title: isRetired ? 'Актив вибув — зміна стану недоступна'
        : `Консервація / ремонт / повернення, зміна строку чи параметрів амортизації${kind === 'FIXED_ASSET' ? ', модернізація' : ''} — вид операції обирається у документі`,
      disabled: isRetired,
      onClick: () => navigate(
        `/assets/state-change/new?asset_id=${asset!.id}&organization_id=${asset!.organization_id}`
        + `&kind=${status === 'OPERATING' ? 'CONSERVATION' : status === 'PARKED' ? 'USEFUL_LIFE' : 'OPERATING'}`
        + `&kind_asset=${kind}`),
    }] : []),
    { label: 'списання', disabled: isRetired, onClick: () => act('/assets/write-off') },
    ...(kind === 'FIXED_ASSET' ? [{ label: 'продаж', disabled: isRetired,
      onClick: () => navigate(`/goods-sales/new?asset_id=${id}&organization_id=${orgIdEff}`) }] : []),
    { label: 'журнал амортизації', disabled: isLv,
      onClick: () => navigate(`/assets/depreciation-journal?organization_id=${asset!.organization_id}&kind=${kind}`) },
    { label: 'відомості про актив',
      onClick: () => navigate(`/assets/info?organization_id=${asset!.organization_id}&kind=${kind}&asset_id=${asset!.id}`) },
  ];

  // 2026-08-31 (структуризація картки): параметри, зафіксовані введенням,
  // блокуються — зміни лише документами.
  const introduced = !!asset && status !== 'PARKED';
  const act = (route: string, extra = '') => {
    // Стандартні редактори (2026-08-29): усі документи → /new з prefill.
    navigate(`${route}/new?asset_id=${asset!.id}&kind=${kind}&organization_id=${asset!.organization_id}${extra}`);
  };

  // ч.915: прогрес рейки — ЛИШЕ обовʼязкові поля (умова canSave): організація і
  // назва; для ОЗ/НМА — ще група і метод амортизації. Порядок = порядок DocBlock
  // нижче. Блоки вкладок «Рахунки обліку»/«Документи» є лише у збереженої
  // картки (як і самі вкладки); «Податковий облік» (УКТ ЗЕД) — не для малоцінки.
  const costTitle = isLv ? 'Вартість і списання' : 'Вартість і амортизація';
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (orgIdEff ? 1 : 0) + (f.name.trim() ? 1 : 0) },
    { id: 'cost', title: costTitle, required: isLv ? 0 : 2,
      filled: isLv ? 0 : (f.asset_group_id ? 1 : 0) + (f.depreciation_method_id ? 1 : 0) },
    { id: 'place', title: 'Розміщення і відповідальний', required: 0, filled: 0 },
    ...(!isLv ? [{ id: 'tax', title: 'Податковий облік', required: 0, filled: 0 }] : []),
    ...(asset ? [
      { id: 'accounts', title: 'Рахунки обліку', required: 0, filled: 0 },
      { id: 'docs', title: 'Документи', required: 0, filled: 0 },
    ] : []),
    { id: 'extra', title: 'Коментар', required: 0, filled: 0 },
  ];

  // ч.916 (зауваження власника): форма розбита на блоки → блоки НУМЕРУЮТЬСЯ,
  // як у документах; номер = позиція у `blocks` (збігається з правою колонкою).
  const num = (bid: string) => blocks.findIndex((b) => b.id === bid) + 1;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          {isNew ? `Нова картка ${ASSET_KIND_UA[kind]}` : `${ASSET_KIND_UA[kind]}: ${asset?.name ?? '…'}`}
          {asset && <ImportedBadge source={asset.external_source} />}
        </h2>
        {/* ч.639 (рішення власника): дії картки — на нижній статичній плашці
            DocumentToolbar. ч.915 (новий дизайн, Закон #10): колишні «ще дії»
            (документи по активу) — у «Повʼязаних діях» правої рейки (railMode);
            обгортка тримає інфо-рядок тулбара праворуч у рядку назви. */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/assets/registry')}
          onSave={() => save.mutate()}
          saveDisabled={!canSave} savePending={save.isPending}
          postable={false}
          isNew={isNew}
          deletable={!!asset && !isRetired}
          onDelete={() => { if (confirm('Позначити картку на видалення?')) remove.mutate(); }}
          onPrint={asset ? () => navigate(`/assets/info?organization_id=${asset.organization_id}&kind=${kind}&asset_id=${asset.id}`) : undefined}
          printDisabled={!asset}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      {/* ч.915: смуга стану активу — банер НАД DocLayout (на всю ширину картки). */}
      {asset && (
        <div style={{
          display: 'flex', gap: '1.5rem', flexWrap: 'wrap', padding: '0.5rem 0.75rem',
          background: 'var(--bg-soft)', borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.9rem',
        }}>
          <span>Статус: <b>{ASSET_STATUS_UA[asset.status] ?? asset.status}</b></span>
          <span>Первісна: <b>{fmt2(asset.primary_cost)}</b></span>
          <span>Знос: <b>{fmt2(asset.accumulated_depreciation ?? asset.cost_written_off)}</b></span>
          {!isLv && <span>Залишкова: <b>{fmt2(asset.residual_value)}</b></span>}
          {asset.commissioning_date && <span>Введено: <b>{new Date(asset.commissioning_date).toLocaleDateString('uk-UA')}</b></span>}
          {!isLv && asset.elapsed_depreciation_periods != null && (
            <span>Періодів амортизації: <b>{asset.elapsed_depreciation_periods}</b> з {asset.useful_life_months}</span>
          )}
        </div>
      )}

      {/* ч.915 (новий дизайн, Закон #10): ліворуч — блоки без номерів (картка
          довідника), праворуч — рейка: прогрес обовʼязкових полів, «Повʼязані
          дії» (документи по активу) та «Історія документа» (без «Звʼязаних»). */}
      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew} related={false}
          doc={{ type: ENTITY_BY_KIND[kind], id: isNew ? '' : id! }} />
      }>
        {/* ч.915: вкладки картки працюють як і раніше (той самий стан `tab`, ті
            самі три вкладки); вміст кожної вкладки — у блоках DocBlock нижче.
            Flex-обгортка — щоб сегмент не розтягувався на всю ширину колонки. */}
        {asset && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div className="seg-tabs" style={{ margin: 0 }}>
              <button type="button" className={tab === 'main' ? 'active' : ''} onClick={() => setTab('main')}>Реквізити</button>
              <button type="button" className={tab === 'accounts' ? 'active' : ''} onClick={() => setTab('accounts')}>Рахунки обліку</button>
              <button type="button" className={tab === 'docs' ? 'active' : ''} onClick={() => setTab('docs')}>
                Документи ({docRows.length})</button>
            </div>
          </div>
        )}

        {/* ч.915: вкладка «Реквізити» — чотири логічні блоки; вибулий актив
            блокується fieldset-ом у кожному з них (як раніше — одним на форму). */}
        {tab === 'main' && (
          <DocBlock index={num('main')} title="Основні дані">
            <fieldset disabled={isRetired} style={FIELDSET_STYLE}>
              <div className="doc-grid">
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Організація <span style={{ color: 'red' }}>*</span></label>
                  <select value={orgIdEff} disabled={!!asset}
                    onChange={(e) => setF({ ...f, organization_id: e.target.value })}>
                    {availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </div>
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Інвентарний № <small style={{ color: 'var(--muted)' }}>(порожньо — авто)</small></label>
                  <input value={f.inventory_number} onChange={(e) => setF({ ...f, inventory_number: e.target.value })} />
                </div>
              </div>
              <div className="field">
                <label>Назва <span style={{ color: 'red' }}>*</span></label>
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
              </div>
            </fieldset>
          </DocBlock>
        )}

        {/* ч.915: параметри, що фіксуються введенням в експлуатацію (змінюються
            лише документами), — одним блоком; для малоцінки — «Вартість і списання». */}
        {tab === 'main' && (
          <DocBlock index={num('cost')} title={costTitle}>
            <fieldset disabled={isRetired} style={FIELDSET_STYLE}>
              {!isLv && (
                <div className="doc-grid">
                  <div className="field" style={{ gridColumn: 'span 6' }}>
                    <label>Група <span style={{ color: 'red' }}>*</span></label>
                    <select value={f.asset_group_id} disabled={introduced}
                      title={introduced ? 'Група зафіксована введенням (рахунок/мін. строк) — зміна лише через розпроведення введення' : undefined}
                      onChange={(e) => setF({ ...f, asset_group_id: e.target.value })}>
                      <option value="">— оберіть —</option>
                      {(groupsQ.data?.items ?? []).map((g) => (
                        <option key={g.id} value={g.id}>{g.group_number}. {g.name_uk} ({g.accounting_account_code})</option>
                      ))}
                    </select>
                  </div>
                  <div className="field" style={{ gridColumn: 'span 6' }}>
                    <label>Метод амортизації <span style={{ color: 'red' }}>*</span></label>
                    <select value={f.depreciation_method_id} disabled={introduced}
                      title={introduced ? 'Для введеного активу — «Зміна стану: зміна параметрів амортизації»' : undefined}
                      onChange={(e) => setF({ ...f, depreciation_method_id: e.target.value })}>
                      <option value="">— оберіть —</option>
                      {(methodsQ.data?.items ?? []).map((m) => <option key={m.id} value={m.id}>{m.name_uk}</option>)}
                    </select>
                  </div>
                </div>
              )}
              <div className="doc-grid">
                <div className="field" style={{ gridColumn: 'span 4' }}>
                  <label>Первісна вартість</label>
                  <input type="number" step="0.01" value={f.primary_cost} style={{ textAlign: 'right' }}
                    disabled={!!asset && status !== 'PARKED'}
                    onChange={(e) => setF({ ...f, primary_cost: e.target.value })} />
                </div>
                {!isLv ? (
                  <>
                    <div className="field" style={{ gridColumn: 'span 4' }}>
                      <label>Ліквідаційна вартість</label>
                      <input type="number" step="0.01" value={f.liquidation_value} style={{ textAlign: 'right' }}
                        disabled={introduced}
                        title={introduced ? 'Для введеного активу — «Зміна стану: зміна параметрів амортизації»' : undefined}
                        onChange={(e) => setF({ ...f, liquidation_value: e.target.value })} />
                    </div>
                    <div className="field" style={{ gridColumn: 'span 4' }}>
                      <label>Строк корисного викор., міс</label>
                      <input type="number" min="1" value={f.useful_life_months}
                        disabled={!!asset && status !== 'PARKED'}
                        title={asset && status !== 'PARKED' ? 'Для введеного активу — через дію «змінити строк»' : undefined}
                        onChange={(e) => setF({ ...f, useful_life_months: e.target.value })} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="field" style={{ gridColumn: 'span 4' }}>
                      <label>Метод списання</label>
                      <select value={f.write_off_method} disabled={introduced}
                        title={introduced ? 'Метод зафіксовано введенням (частину вже списано) — зміна лише через розпроведення введення' : undefined}
                        onChange={(e) => setF({ ...f, write_off_method: e.target.value })}>
                        <option value="SPLIT_50_50">50/50</option>
                        <option value="AT_USE_100">100% при введенні</option>
                      </select>
                    </div>
                    <div className="field" style={{ gridColumn: 'span 4' }}>
                      <label>Кількість</label>
                      <input type="number" min="1" value={f.quantity} disabled={introduced}
                        title={introduced ? 'Кількість зафіксована введенням в експлуатацію' : undefined}
                        onChange={(e) => setF({ ...f, quantity: e.target.value })} />
                    </div>
                  </>
                )}
              </div>
              {asset && (
                <p style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
                  Накопичений знос і залишкова вартість змінюються лише документами
                  (введення, амортизація, модернізація, списання) — не карткою.
                </p>
              )}
            </fieldset>
          </DocBlock>
        )}

        {tab === 'main' && (
          <DocBlock index={num('place')} title="Розміщення і відповідальний">
            <fieldset disabled={isRetired} style={FIELDSET_STYLE}>
              <div className="doc-grid">
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Підрозділ{introduced && <button type="button" style={{ marginLeft: 6, padding: '0 0.5rem', fontSize: '0.75rem' }}
                    title="Для введеного активу підрозділ/МВО змінюються документом «Внутрішнє переміщення»"
                    onClick={() => act('/assets/transfer')}>перемістити</button>}</label>
                  <select value={f.department_id} disabled={introduced}
                    title={introduced ? 'Змінюється документом «Внутрішнє переміщення»' : undefined}
                    onChange={(e) => setF({ ...f, department_id: e.target.value })}>
                    <option value="">—</option>
                    {(departmentsQ.data ?? []).map((d: { id: string; name: string }) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>МВО (відповідальний)</label>
                  <select value={f.responsible_employee_id} disabled={introduced}
                    title={introduced ? 'Змінюється документом «Внутрішнє переміщення»' : undefined}
                    onChange={(e) => setF({ ...f, responsible_employee_id: e.target.value })}>
                    <option value="">—</option>
                    {(employeesQ.data ?? []).map((e: { id: string; full_name: string | null }) => (
                      <option key={e.id} value={e.id}>{e.full_name ?? e.id}</option>
                    ))}
                  </select>
                </div>
              </div>
            </fieldset>
          </DocBlock>
        )}

        {/* ч.915: УКТ ЗЕД (для ПН при продажу) — лише ОЗ/НМА, як і раніше (!isLv). */}
        {tab === 'main' && !isLv && (
          <DocBlock index={num('tax')} title="Податковий облік">
            <fieldset disabled={isRetired} style={FIELDSET_STYLE}>
              {/* ч.1388: амортизація за податковими правилами — для додатка АМ і різниць РІ
                  декларації з прибутку. Порожній строк / вартість = як у бухобліку. */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <input type="checkbox" checked={f.tax_depreciable} style={{ width: 'auto' }}
                  onChange={(e) => setF({ ...f, tax_depreciable: e.target.checked })} />
                амортизується в податковому обліку (виробничий актив)
              </label>
              <div className="doc-grid">
                <div className="field" style={{ gridColumn: 'span 4' }}>
                  <label>Податковий строк, міс <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(порожньо — більший з бухгалтерського і мінімального за групою{_grp?.min_useful_life_months ? `, ${_grp.min_useful_life_months}` : ''})</span></label>
                  <input type="number" min="1" value={f.tax_useful_life_months} disabled={!f.tax_depreciable}
                    placeholder={String(Math.max(Number(f.useful_life_months) || 0, _grp?.min_useful_life_months ?? 0) || '')}
                    onChange={(e) => setF({ ...f, tax_useful_life_months: e.target.value })} />
                </div>
                <div className="field" style={{ gridColumn: 'span 4' }}>
                  <label>Податкова первісна вартість <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(порожньо — як у бухобліку)</span></label>
                  <input type="number" step="0.01" value={f.tax_primary_cost} style={{ textAlign: 'right' }} disabled={!f.tax_depreciable}
                    placeholder={f.primary_cost} onChange={(e) => setF({ ...f, tax_primary_cost: e.target.value })} />
                </div>
                <div className="field" style={{ gridColumn: 'span 4' }}>
                  <label>Накопичена податкова амортизація</label>
                  <input type="number" step="0.01" value={f.tax_accumulated_depreciation} style={{ textAlign: 'right' }}
                    disabled={!!asset && status !== 'PARKED'}
                    title={asset && status !== 'PARKED' ? 'Для введеного активу її пишуть документи амортизації' : 'Для картки з історією (перенесеної з 1С) впишіть накопичену за податковими правилами'}
                    onChange={(e) => setF({ ...f, tax_accumulated_depreciation: e.target.value })} />
                </div>
              </div>
              {kind === 'FIXED_ASSET' && [3, 4, 5, 9].includes(_grp?.group_number ?? 0) && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '4px 0 8px' }}>
                  <input type="checkbox" checked={f.tax_accelerated} style={{ width: 'auto' }} disabled={!f.tax_depreciable}
                    onChange={(e) => setF({ ...f, tax_accelerated: e.target.checked })} />
                  прискорена амортизація за мінімально допустимим строком ({[4, 5].includes(_grp?.group_number ?? 0) ? '2 роки' : '5 років'}, прямолінійно; лише новий актив, введений з 2020 року)
                </label>
              )}
              {asset && (
                <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: '0 0 8px' }}>
                  Податкова залишкова: <b>{fmt2(Math.max(0, Number((asset as { tax_primary_cost?: string | null }).tax_primary_cost ?? asset.primary_cost) - Number((asset as { tax_accumulated_depreciation?: string }).tax_accumulated_depreciation ?? 0)))}</b>.
                  Суми податкової амортизації по місяцях — у документах амортизації; підсумки за період — у додатку АМ декларації з податку на прибуток.
                </p>
              )}
              <div className="field">
                <label>Код УКТ ЗЕД <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(для податкової накладної при продажу)</span></label>
                <input value={f.ukt_zed_code} maxLength={20}
                  onChange={(e) => setF({ ...f, ukt_zed_code: e.target.value })} placeholder="8427 10 10 00" />
              </div>
            </fieldset>
          </DocBlock>
        )}

        {/* 0447 (2026-08-31, рішення власника): рахунки обліку — окрема вкладка.
            Порожнє поле = діє наступний рівень: налаштування орг > група > типовий. */}
        {tab === 'accounts' && (
          <DocBlock index={num('accounts')} title="Рахунки обліку">
            <fieldset disabled={false} style={FIELDSET_STYLE}>
              {introduced && (f.account_code.trim() !== ((asset as { account_code?: string | null })?.account_code ?? '')
                || (!isLv && f.depreciation_account_code.trim() !== ((asset as { depreciation_account_code?: string | null })?.depreciation_account_code ?? ''))) && (
                <div style={{ background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)', color: 'var(--badge-amber-fg)',
                  borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '0.6rem', fontSize: '0.85rem' }}>
                  ⚠ Актив уже введено в експлуатацію: сальдо обліковується на попередньому
                  рахунку. Після збереження або розпровести і провести введення заново,
                  або перенести сальдо довідкою — інакше рахунок не закриється.
                </div>
              )}
              <div className="doc-grid" style={{ maxWidth: 860 }}>
                <div className="field" style={{ gridColumn: 'span 4' }}>
                  <label>Рахунок обліку активу</label>
                  <input value={f.account_code} maxLength={10} placeholder={effAcc.code}
                    onChange={(e) => setF({ ...f, account_code: e.target.value })} />
                  <span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>діє: {effAcc.code} ({effAcc.src})</span>
                </div>
                {!isLv && (
                  <div className="field" style={{ gridColumn: 'span 4' }}>
                    <label>Рахунок зносу</label>
                    <input value={f.depreciation_account_code} maxLength={10} placeholder={effDepr.code}
                      onChange={(e) => setF({ ...f, depreciation_account_code: e.target.value })} />
                    <span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>діє: {effDepr.code} ({effDepr.src})</span>
                  </div>
                )}
                <div className="field" style={{ gridColumn: 'span 4' }}>
                  <label>Податкове призначення (ПДВ)</label>
                  <select value={f.tax_purpose_id}
                    onChange={(e) => setF({ ...f, tax_purpose_id: e.target.value })}>
                    <option value="">— не задано —</option>
                    {(taxPurposesQ.data ?? []).map((p) => (
                      <option key={p.id} value={p.id}>{p.name_uk}</option>
                    ))}
                  </select>
                  <span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
                    міняється документом «Податкове призначення» (з ПДВ-коригуванням 198.5)
                  </span>
                </div>
                <div className="field" style={{ gridColumn: 'span 4' }}>
                  <label>Рахунок витрат {isLv ? 'списання' : 'амортизації'}</label>
                  <select value={f.expense_account_code} onChange={(e) => setF({ ...f, expense_account_code: e.target.value })}>
                    {!isLv && <option value="23">23 — Виробництво</option>}
                    <option value="91">91 — Загальновиробничі</option>
                    <option value="92">92 — Адміністративні</option>
                    <option value="93">93 — Збут</option>
                    <option value="94">94 — Інші операційні</option>
                  </select>
                </div>
              </div>
              <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginTop: '0.5rem' }}>
                Порожнє поле — рахунок береться з налаштувань «Рахунки обліку НА», далі зі
                {isLv ? ' стандарту малоцінки (22)' : ' стандарту групи'}.{!isLv && ' Рахунок капітальних інвестицій (152/154) — фіксований.'}
                {' '}Зберігається кнопкою «зберегти». Уже проведені документи не змінюються —
                за потреби розпровести і провести заново.
              </p>
            </fieldset>
          </DocBlock>
        )}

        {/* ч.915: вкладка «Документи» — таблиця документів по активу, без змін. */}
        {tab === 'docs' && (
          <DocBlock index={num('docs')} title="Документи">
            <TchTable>
              <thead><tr><th>Дата</th><th>Документ</th><th>№</th><th>Примітка</th>
                <th style={{ textAlign: 'right' }}>Сума</th><th>Проведено</th></tr></thead>
              <tbody>
                {docRows.length === 0 && <tr><td colSpan={6} style={{ color: 'var(--muted)' }}>Документів по активу немає.</td></tr>}
                {docRows.map((d, i) => {
                  const meta = DOC_TYPE_UA[d.doc_type];
                  const is1c = d.doc_type === '1c_event';
                  // Довідка з проводками документа 1С — відкриваємо САМ документ.
                  const href = d.doc_type === 'manual_operation' && d.doc_id
                    ? `/manual-operations/${d.doc_id}` : null;
                  const printHref = d.doc_type === 'fixed_asset_inventory' && d.doc_id
                    ? `/assets/inventory/${d.doc_id}/print` : null;
                  return (
                    <tr key={`${d.doc_id ?? 'ev'}-${i}`}>
                      <td>{d.document_date ? new Date(d.document_date).toLocaleDateString('uk-UA') : ''}</td>
                      <td>
                        {is1c
                          ? <span title="Документ існує лише в 1С — його проводки не імпортовано (або їх немає)">
                              <ImportedBadge source="1c" /> {d.title ?? 'Подія 1С'}</span>
                          : href
                            ? <><ImportedBadge source="1c" /> <a href="#" onClick={(e) => { e.preventDefault(); navigate(href); }}>{d.title ?? meta?.label}</a></>
                            : printHref
                            ? <><ImportedBadge source="1c" /> <a href="#" onClick={(e) => { e.preventDefault(); navigate(printHref); }}>{d.title ?? meta?.label}</a></>
                            : <a href="#" onClick={(e) => { e.preventDefault();
                                const direct: Record<string, string> = {
                                  asset_commissioning: '/assets/commissioning', asset_write_off: '/assets/write-off', fixed_asset_sale: '/assets/sale',
                                  asset_internal_transfer: '/assets/transfer', fixed_asset_inventory: '/assets/inventory',
                                  fixed_asset_modernization: '/assets/state-change/m', asset_state_change: '/assets/state-change/s',
                                };
                                const base = direct[d.doc_type];
                                navigate(base && d.doc_id ? `${base}/${d.doc_id}` : (meta?.route ?? '/assets/registry')); }}>
                                {meta?.label ?? d.doc_type}</a>}
                      </td>
                      <td>{d.document_number}</td>
                      <td>{is1c ? (d.note ?? '') : d.note ? (ASSET_STATUS_UA[d.note] ?? d.note) : ''}</td>
                      <td style={{ textAlign: 'right' }}>{d.amount != null ? fmt2(d.amount) : ''}</td>
                      <td>{d.posted ? '✓' : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </TchTable>
          </DocBlock>
        )}

        {/* ч.915: останній блок — коментар (видно на всіх вкладках, як у картках
            працівника/фізособи). Панелі вкладень картка активу НЕ мала — нової
            не додаємо (чистий ре-лейаут), тому блок лише з коментарем. */}
        <DocBlock index={num('extra')} title="Коментар">
          <fieldset disabled={isRetired} style={FIELDSET_STYLE}>
            <div className="field">
              <label>Коментар</label>
              <input value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} />
            </div>
          </fieldset>
        </DocBlock>
      </DocLayout>
      {error && <div style={{ color: '#dc2626', marginTop: 8, whiteSpace: 'pre-wrap' }}>{error}</div>}
    </div>
  );
}


function emptyForm() {
  return {
    organization_id: '', inventory_number: '', name: '', asset_group_id: '',
    depreciation_method_id: '', primary_cost: '0', liquidation_value: '0',
    useful_life_months: '60', expense_account_code: '92', department_id: '', tax_purpose_id: '',
    responsible_employee_id: '', comment: '', ukt_zed_code: '', account_code: '', depreciation_account_code: '', write_off_method: 'SPLIT_50_50', quantity: '1',
    tax_depreciable: true, tax_useful_life_months: '', tax_primary_cost: '', tax_accumulated_depreciation: '0', tax_accelerated: false,
  };
}
function fromAsset(a: AnyAsset) {
  return {
    organization_id: a.organization_id ?? '',
    inventory_number: a.inventory_number ?? '',
    name: a.name ?? '',
    asset_group_id: a.asset_group_id ?? '',
    depreciation_method_id: a.depreciation_method_id ?? '',
    primary_cost: a.primary_cost ?? '0',
    liquidation_value: a.liquidation_value ?? '0',
    useful_life_months: String(a.useful_life_months ?? 60),
    expense_account_code: (a.expense_account_code ?? '92') as string,
    tax_purpose_id: (a as { tax_purpose_id?: string | null }).tax_purpose_id ?? '',
    department_id: a.department_id ?? '',
    responsible_employee_id: (a as { responsible_employee_id?: string | null }).responsible_employee_id ?? '',
    comment: (a as { comment?: string | null }).comment ?? '',
    ukt_zed_code: (a as { ukt_zed_code?: string | null }).ukt_zed_code ?? '',
    account_code: (a as { account_code?: string | null }).account_code ?? '',
    depreciation_account_code: (a as { depreciation_account_code?: string | null }).depreciation_account_code ?? '',
    write_off_method: (a.write_off_method ?? 'SPLIT_50_50') as string,
    quantity: String(a.quantity ?? 1),
    tax_depreciable: (a as { tax_depreciable?: boolean }).tax_depreciable ?? true,
    tax_useful_life_months: (a as { tax_useful_life_months?: number | null }).tax_useful_life_months != null
      ? String((a as { tax_useful_life_months?: number | null }).tax_useful_life_months) : '',
    tax_primary_cost: (a as { tax_primary_cost?: string | null }).tax_primary_cost ?? '',
    tax_accumulated_depreciation: (a as { tax_accumulated_depreciation?: string }).tax_accumulated_depreciation ?? '0',
    tax_accelerated: (a as { tax_accelerated?: boolean }).tax_accelerated ?? false,
  };
}

